import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import PageSEO from '@/components/feature/PageSEO';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'updated'>('updated');
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const activeCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics = useMemo(() => {
    let result = [...comics];

    // Filter by category
    if (slug && slug !== 'all') {
      result = result.filter((c) => c.category === slug);
    }

    // Filter by tag
    if (activeTag) {
      result = result.filter((c) => c.tags.includes(activeTag));
    }

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'views') return b.views - a.views;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

    return result;
  }, [slug, activeTag, searchQuery, sortBy]);

  // Get available tags for current category
  const availableTags = useMemo(() => {
    const base = slug && slug !== 'all' ? comics.filter((c) => c.category === slug) : comics;
    const tagSet = new Set<string>();
    base.forEach((c) => c.tags.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet);
  }, [slug]);

  // Reset active tag when category changes
  useEffect(() => {
    setActiveTag(null);
  }, [slug]);

  const catLabel = activeCategory.name;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catLabel}韩漫 - 韩漫网`,
    description: `韩漫网${catLabel}漫画专区，提供高清下拉式${catLabel}韩漫在线阅读，海量${catLabel}题材漫画大全。`,
    url: `https://douxinkeji.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'https://douxinkeji.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <PageSEO
        title={`${catLabel}韩漫_${catLabel}漫画免费在线阅读_韩漫网`}
        description={`韩漫网${catLabel}漫画专区，提供高清下拉式${catLabel}韩漫在线阅读，海量${catLabel}题材漫画大全，画质清晰阅读流畅。`}
        keywords={`${catLabel}韩漫,${catLabel}漫画,免费韩漫,韩漫网`}
        canonicalUrl={`https://douxinkeji.cn/category/${slug}`}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar onSearchChange={setSearchQuery} />

        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '漫画分类', path: '/category/all' },
            { label: catLabel },
          ]}
        />

        {/* Category Tabs */}
        <section className="px-4 md:px-6 py-4 bg-background-100">
          <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
            <strong>漫画分类</strong>
          </h1>
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                  activeCategory.slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-primary-100'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Filter & Sort Bar */}
        <section className="px-4 md:px-6 py-4 border-b border-background-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setActiveTag(null)}
                className={`px-3 py-1 rounded-full text-xs transition-all cursor-pointer whitespace-nowrap ${
                  !activeTag
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                }`}
              >
                全部
              </button>
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className={`px-3 py-1 rounded-full text-xs transition-all cursor-pointer whitespace-nowrap ${
                    activeTag === tag
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-foreground-500">排序：</span>
              {[
                { key: 'updated' as const, label: '最新' },
                { key: 'rating' as const, label: '评分' },
                { key: 'views' as const, label: '人气' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1 rounded-full text-xs transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Comic Grid */}
        <section className="px-4 md:px-6 py-6">
          {filteredComics.length > 0 ? (
            <>
              <p className="text-sm text-foreground-500 mb-4">
                共找到 <strong className="text-foreground-800">{filteredComics.length}</strong> 部漫画
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">暂无符合条件的漫画</p>
              <p className="text-foreground-400 text-xs mt-1">请尝试更换筛选条件</p>
            </div>
          )}
        </section>

        <Footer />
      </div>
    </>
  );
}